<template>
  <div>
    <modal v-show="isTreatModalVisible" @close="closeTreatModal" modalIsLarge footer>
      <template v-slot:title>
        <h5>Treat Kiddies Account Request</h5>
      </template>
      <template v-slot:body>
        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label>Status</label>
              <select class="form-control" v-model="treatStatus">
                <option value="" disabled>Please select</option>
                <option value="approved">Approve</option>
                <option value="rejected">Reject</option>
              </select>
            </div>
          </div>
          <div class="col-md-12">
            <div class="form-group">
              <label>Reason</label>
              <textarea class="form-control" v-model="treatReason" rows="4" style="resize: none"></textarea>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <button class="button" :disabled="treatLoading || !treatStatus" @click.prevent="confirmAndTreat">
          <span class="custom-loader" v-if="treatLoading">Please wait...</span>
          <span v-else>Proceed</span>
        </button>
        <button class="btn btn-secondary" @click="closeTreatModal">Cancel</button>
      </template>
    </modal>
    <div
      v-if="loadingAccount"
      style="display: flex; top: 0px; position: fixed; left: 0px; right: 0px; bottom: 0px; background-color: rgba(247, 247, 247, 0.9); z-index: 9999; justify-content: center; align-items: center;"
    >
      <div>
        <div>
          <h3>Loading account details...</h3>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-2">
        <div class="card mb-3">
          <div class="card-header">Profile Image</div>
          <div class="card-body text-center">
            <img
              v-if="account && account.image"
              :src="account.image"
              alt="Profile"
              class="img-fluid rounded"
            />
            <div v-else class="text-muted">No image available</div>
          </div>
        </div>

        <div class="card">
          <div class="card-header">NIN Image</div>
          <div class="card-body text-center">
            <img
              v-if="ninDetails"
              :src="ninDetails.image"
              alt="NIN"
              class="img-fluid rounded"
            />
            <div v-else class="text-muted">No NIN image available</div>
          </div>
        </div>
      </div>

      <div class="col-md-10">
        <ul class="nav nav-tabs mb-3">
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: activeTab === 1 }"
              href="#"
              @click.prevent="activeTab = 1"
            >
              Overview
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: activeTab === 2 }"
              href="#"
              @click.prevent="activeTab = 2"
            >
              Transactions
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: activeTab === 3 }"
              href="#"
              @click.prevent="activeTab = 3"
            >
              Card Request
            </a>
          </li>
        </ul>

        <div v-if="activeTab === 1">
          <div class="card">
            <div class="card-header d-flex align-items-center justify-content-between">
              <div>Kiddies Account Overview</div>
              <div>
                <button v-if="account && account.status === 'pending' && permissions.includes('account-requests')" class="btn btn-primary btn-sm" @click.prevent="openTreatModal">Treat Request</button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="account">
                <div class="row mb-2">
                  <div class="col-md-4"><strong>First name</strong></div>
                  <div class="col-md-8">{{ account.first_name }}</div>
                </div>
                <div class="row mb-2">
                  <div class="col-md-4"><strong>Last name</strong></div>
                  <div class="col-md-8">{{ account.last_name || '-' }}</div>
                </div>
                <div class="row mb-2">
                  <div class="col-md-4"><strong>Account Number</strong></div>
                  <div class="col-md-8">{{ account.account ? account.account.account_number : 'None Yet' }}</div>
                </div>
                <div class="row mb-2">
                  <div class="col-md-4"><strong>Status</strong></div>
                  <div class="col-md-8">
                    <span class="badge" 
                        :class="{
                          'badge-success': account.status == 'approved',
                          'badge-default':  account.status == 'initiated',
                          'badge-warning':  account.status == 'pending',
                          'badge-danger':  account.status == 'rejected',
                        }"
                        >
                          {{ account.status | capitalize }}
                      </span>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col-md-4"><strong>Date Created</strong></div>
                  <div class="col-md-8">{{ account.created_at.toLocaleString() }}</div>
                </div>
                <div class="row mb-2" v-if="account.user">
                  <div class="col-md-4"><strong>Guardian</strong></div>
                  <div class="col-md-8">{{ `${account.user.first_name} ${account.user.last_name}` }}</div>
                </div>
                <div class="row mb-2">
                  <div class="col-md-4"><strong>DOB</strong></div>
                  <div class="col-md-8">{{ account.date_of_birth || '-' }} <strong class="fw-bold text-dark"><i>{{ account.age }} Years</i>
                </strong> </div>
                </div>
                <div class="row mb-2">
                  <div class="col-md-4"><strong>NIN</strong></div>
                  <div class="col-md-8">{{ account.nin || '-' }}</div>
                </div>
                <div class="row mb-2">
                  <div class="col-md-4"><strong>School Name</strong></div>
                  <div class="col-md-8">{{ account.school_name || '-' }}</div>
                </div>
              </div>
              <div v-else class="text-muted">No account data available</div>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 2">
          <div class="card">
            <div class="card-header">Transactions</div>
            <div class="card-body">
              <div v-if="transactions && transactions.length">
                <div class="table-responsive">
                  <table class="table table-sm table-striped">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Reference</th>
                        <th>Amount</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(t, idx) in transactions" :key="t.id || idx">
                        <td>{{ idx + 1 }}</td>
                        <td>{{ t.transaction_reference || t.reference || '-' }}</td>
                        <td>{{ t.amount !== undefined ? t.amount : '-' }}</td>
                        <td>{{ t.type || '-' }}</td>
                        <td>{{ t.status || '-' }}</td>
                        <td>{{ t.created_at || t.date || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else class="text-muted">No transactions available</div>
            </div>
          </div>
        </div>
        <div v-if="activeTab === 3 && account.card_request">
            <SingleCard :slug="account.card_request.request_reference"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from '@/services/api.service';
import ErrorService from '@/services/error.service';
import SingleCard from '../cards/SingleCard.vue';
import Swal from 'sweetalert2';

export default {
  props: {
    reference: {
      type: String,
      required: true
    }
  },
  components:{
    SingleCard
  },
  data() {
    return {
      account: null,
      transactions: [],
      loadingAccount: false,
      ninLoading: false,
      activeTab: 1,
      ninDetails : {}
  ,isTreatModalVisible: false,
  treatStatus: '',
  treatReason: '',
  treatLoading: false
    };
  },
  computed: {
    permissions() {
      return this.$store.getters.permissions;
    }
  },
  methods: {
    async getAccountDetails() {
      this.loadingAccount = true;
      try {
        const response = await ApiService.get(`account/kiddies_accounts/${this.reference}`);
        this.loadingAccount = false;
        // assume data payload at response.data.data
        this.account = response.data.data || response.data || null;
        // if account has a NIN, attempt to verify and fetch NIN image
        if (this.account && this.account.nin) {
          this.verifyNin(this.account.nin);
        }
        // try common locations for transactions
        this.transactions = (this.account && (this.account.transactions || this.account.external_transactions || [])) || [];
      } catch (error) {
        this.loadingAccount = false;
        if (error && error.response && error.response.data) {
          ErrorService.displayErrorAlert(error.response.data);
        } else {
          console.error(error);
        }
      }
  },
    async verifyNin(nin) {
      if (!nin) return;
      this.ninLoading = true;
      try {
        const payload = { nin };
        const res = await ApiService.post('verification/verify_nin', payload);
        this.ninLoading = false;
        const data = res.data && (res.data.data || res.data);
        // prefer `image` then `captured_image`
        this.ninDetails = data
      } catch (err) {
        this.ninLoading = false;
        if (err && err.response && err.response.data) {
          ErrorService.displayErrorAlert(err.response.data);
        } else {
          console.error(err);
        }
      }
    },
    openTreatModal() {
      // only open if pending
      if (this.account && this.account.status === 'pending') {
        this.isTreatModalVisible = true;
      } else {
        Swal.fire({ icon: 'info', title: 'Not allowed', text: 'Account is not pending' });
      }
    },
    closeTreatModal() {
      this.isTreatModalVisible = false;
      this.treatStatus = '';
      this.treatReason = '';
    },
    async confirmAndTreat() {
      if (!this.treatStatus) return;
      // close modal first
      this.isTreatModalVisible = false;
      const confirm = await Swal.fire({
        title: 'Are you sure?',
        html: `This will mark the account as <strong class="text-capitalize">${this.treatStatus}</strong>.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, continue!',
        cancelButtonText: 'Cancel'
      });

      if (!confirm.isConfirmed) return;
      this.treatAccountRequest();
    },
    async treatAccountRequest() {
      if (!this.account) return;
      //
      this.treatLoading = true;
      try {
        const payload = {
          reference: this.account.reference || this.account.account_number || this.reference,
          status: this.treatStatus,
          reason: this.treatReason
        };
        const res = await ApiService.post('account/treat_kiddies_account_request', payload);
        this.treatLoading = false;
        this.closeTreatModal();
        Swal.fire({ icon: 'success', title: 'Success', text: res.data.message });
        // refresh account
        this.getAccountDetails();
      } catch (err) {
        this.treatLoading = false;
        if (err && err.response && err.response.data) {
          ErrorService.displayErrorAlert(err.response.data);
        } else {
          console.error(err);
        }
      }
    },
  },
  mounted() {
    this.getAccountDetails();
  }
};
</script>

<style scoped>
.card img {
  max-height: 180px;
  object-fit: contain;
}
</style>
